import Table, {
  TableControls,
  TableFooter,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn
} from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import EmptyState from "@/components/EmptyState";
import { ColumnDef } from "@tanstack/react-table";
import Tag from "@/components/Tag";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import type { Path } from "@/router";
import { ReactNode, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { toFormat } from "@/lib/dateHelpers";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import type { MembershipApplicationNode } from "../queries";
import { STAGE_LABELS, APPLICATION_TYPE_LABELS } from "../types";
import type { ApplicationStage, ApplicationTab, ApplicationType } from "../types";
import type { AssignedFilter } from "../_hooks/useApplicationFilters";

const stageTagType: Record<string, "surface" | "information" | "warn" | "success" | "error"> = {
  draft: "surface",
  submitted: "surface",
  under_review: "information",
  changes_requested: "warn",
  approved: "success",
  denied: "error"
};

const STAGE_OPTIONS = [
  { value: "all", label: "Show All" },
  { value: "draft", label: "Draft" },
  { value: "changes_requested", label: "Changes Requested" },
  { value: "submitted", label: "Submitted" },
  { value: "under_review", label: "Under Review" },
  { value: "approved", label: "Approved" },
  { value: "denied", label: "Denied" }
];

const TYPE_OPTIONS = [
  { value: "all", label: "Show All" },
  { value: "member", label: "Member" },
  { value: "affiliate", label: "Affiliate" },
  { value: "associate", label: "Associate" }
];

const SUBMITTED_ON_OPTIONS = [
  { value: "all", label: "Show All" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" }
];

function buildColumns(
  setMenu: (menu: TableRowMenu) => void,
  isCompactLayout: boolean,
  onViewApplication?: (applicationId: string) => void
): ColumnDef<MembershipApplicationNode, unknown>[] {
  return [
    ...buildAvatarDetailsColumn<MembershipApplicationNode>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName,
      getSubtitle: (row) => row.email ?? undefined,
      getPicture: (row) => row.profilePhotoUrl ?? undefined,
      header: "Applicant",
      size: 220,
      id: "applicant"
    }),
    buildCopyTextColumn<MembershipApplicationNode>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId,
      shouldShowCopyButton: (row) => !!row.person.pgaId,
      size: 120
    }),
    {
      id: "type",
      header: "Type",
      accessorFn: (row) => row.applicationType,
      cell: ({ row }) => {
        const type = row.original.applicationType as ApplicationType;
        return APPLICATION_TYPE_LABELS[type] ?? type;
      },
      size: 180
    },
    {
      id: "submitted",
      header: "Submitted",
      accessorFn: (row) => row.submittedAt,
      cell: ({ row }) =>
        row.original.submittedAt ? toFormat(row.original.submittedAt, "MMM dd, yyyy") : renderEmptyCellState(null),
      size: 130
    },
    {
      id: "section",
      header: "Section",
      accessorFn: (row) => row.person.primarySection?.name,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      size: 120
    },
    {
      id: "stage",
      header: "Stage",
      accessorFn: (row) => row.status,
      cell: ({ row }) => {
        const status = row.original.status as ApplicationStage;
        return (
          <Tag tagType={stageTagType[status] ?? "surface"} tagStyle="strong" size="small">
            {STAGE_LABELS[status] ?? status}
          </Tag>
        );
      },
      size: 140
    },
    buildActionColumn<MembershipApplicationNode>({
      getActions: (application) => [
        {
          text: "View Application",
          onClick: () => onViewApplication?.(application.id)
        }
      ],
      onViewClick: onViewApplication ? (application) => onViewApplication(application.id) : undefined,
      viewButtonTitle: "View application details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}

export type ApplicationsTableFilters = {
  searchValue: string;
  setSearchValue: (value: string) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
  stageFilter: string;
  setStageFilter: (value: string) => void;
  sectionFilter: string;
  setSectionFilter: (value: string) => void;
  submittedOnFilter: string;
  setSubmittedOnFilter: (value: string) => void;
  assigned: AssignedFilter | undefined;
  toggleAssigned: (value: AssignedFilter) => void;
  filterState: Record<string, boolean>;
  activeFilterCount: number;
  onClearAllFilters: () => void;
};

export type Props = {
  loading: boolean;
  data: MembershipApplicationNode[];
  setMenu: (menu: TableRowMenu) => void;
  createHeaderPagination?: ReactNode;
  createFooterPagination?: ReactNode;
  onViewApplication?: (applicationId: string) => void;
  activeTab: ApplicationTab;
  filters: ApplicationsTableFilters;
};

export default function ApplicationsTable({
  loading,
  data,
  setMenu,
  createHeaderPagination,
  createFooterPagination,
  onViewApplication,
  activeTab,
  filters
}: Props) {
  const { isCompactLayout } = useIsCompactLayout();
  const showTypeFilter = activeTab === "all";

  const { data: sectionsData } = useQuery<{ sections: { id: string; name: string }[] }>(ALL_SECTIONS_QUERY);
  const sectionOptions = useMemo(
    () => [
      { value: "all", label: "Show All" },
      ...(sectionsData?.sections ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((section) => ({ value: section.id, label: section.name }))
    ],
    [sectionsData?.sections]
  );

  const hasActiveFilters = filters.activeFilterCount > 0 || !!filters.searchValue.trim();

  return (
    <Table
      loading={loading}
      data={data}
      columns={buildColumns(setMenu, isCompactLayout, onViewApplication)}
      getRowLink={(row) => ({
        to: `/people/membership-services/applications/${row.original.id}` as Path
      })}
      data-testid="applications-table"
      hasActiveFilters={hasActiveFilters}
      renderNoDataEmptyState={() => (
        <EmptyState
          iconName="scan_delete"
          title="No Applications"
          caption="There are no membership applications to display at this time."
        />
      )}
      renderFilteredEmptyState={() => (
        <EmptyState iconName="search" title="No Results Found" caption="Try a different search or filter." />
      )}
      renderControls={(_table, { onSortClick }) => (
        <TableControls
          pagination={createHeaderPagination}
          onSortClick={onSortClick}
          useExpandedFilters={true}
          expandedFiltersState={filters.filterState}
          onClearAllFilters={filters.onClearAllFilters}
          activeFilters={filters.activeFilterCount}
          filterChips={[
            {
              label: "Show My Assigned Only",
              onClick: () => filters.toggleAssigned("me"),
              selected: filters.assigned === "me"
            },
            {
              label: "Show Unassigned Only",
              onClick: () => filters.toggleAssigned("unassigned"),
              selected: filters.assigned === "unassigned"
            }
          ]}
          mutuallyExclusiveChips
        >
          <SearchInput
            placeholder="Search Applications"
            query={filters.searchValue}
            onChange={filters.setSearchValue}
          />
          {showTypeFilter ? (
            <div data-name="type" data-filter-size="custom" className="w-52">
              <Select
                label="Application Type"
                options={TYPE_OPTIONS}
                value={filters.typeFilter}
                onSelect={filters.setTypeFilter}
              />
            </div>
          ) : null}
          <div data-name="stage" data-filter-size="custom" className="w-52">
            <Select
              label="Stage"
              options={STAGE_OPTIONS}
              value={filters.stageFilter}
              onSelect={filters.setStageFilter}
            />
          </div>
          <div data-name="section" data-filter-size="custom" className="w-52">
            <Select
              label="Section"
              options={sectionOptions}
              value={filters.sectionFilter}
              onSelect={filters.setSectionFilter}
            />
          </div>
          <div data-name="submittedOn" data-filter-size="custom" className="w-80">
            <Select
              label="Submitted On"
              options={SUBMITTED_ON_OPTIONS}
              value={filters.submittedOnFilter}
              onSelect={filters.setSubmittedOnFilter}
            />
          </div>
        </TableControls>
      )}
      renderFooter={() => <TableFooter pagination={createFooterPagination} />}
    />
  );
}
